import { memo, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import OfferCard from 'components/OfferList/OfferCards/OfferCard'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import BookedLabel from 'tripPlanner/components/BookedLabel'

const HotelCardWrapper = styled.div`
  position: relative;
`

const ClickOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: pointer;
`

const AddToTripButtonContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
  z-index: 2;
`

const BookedLabelContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  left: ${rem(12)};
  z-index: 2;
`

interface Props {
  offerId: string
  index: number
  cardType?: 'hotel' | 'experience'
  offerName?: string
  offerType?: string
  offerCity?: string
  offerCoordinates?: { lat: number; lng: number }
  itineraryData?: TripItineraryData
  isBooked?: boolean
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onOfferClick?: (offerId: string) => void
  onOfferHover?: (offerId: string | null) => void
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filterNights?: number
}

function ChatHotelCarouselCard({
  offerId,
  index,
  cardType = 'hotel',
  offerName,
  offerType,
  offerCity,
  offerCoordinates,
  itineraryData,
  isBooked,
  onSendMessage,
  onOfferClick,
  onOfferHover,
  selectedDatesAndRoomsFilter,
  filterNights,
}: Props) {
  const handleOverlayClick = useCallback(() => {
    onOfferClick?.(offerId)
  }, [onOfferClick, offerId])

  const handleMouseEnter = useCallback(() => {
    onOfferHover?.(offerId)
  }, [onOfferHover, offerId])

  const handleMouseLeave = useCallback(() => {
    onOfferHover?.(null)
  }, [onOfferHover])

  const placeName = offerName || offerId

  return (
    <HotelCardWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {onOfferClick && <ClickOverlay onClick={handleOverlayClick} />}
      {isBooked && (
        <BookedLabelContainer>
          <BookedLabel />
        </BookedLabelContainer>
      )}
      {onSendMessage && cardType === 'experience' && itineraryData && !isBooked && (
        <AddToTripButtonContainer>
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={placeName}
            offerId={offerId}
            offerType={offerType}
            coordinates={offerCoordinates}
            variant="filled"
          />
        </AddToTripButtonContainer>
      )}
      {onSendMessage && cardType !== 'experience' && !isBooked && (
        <AddToTripButtonContainer>
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={placeName}
            offerId={offerId}
            offerType={offerType}
            hotelCity={offerCity}
            hotelCoordinates={offerCoordinates}
            onSendMessage={onSendMessage}
            filterNights={filterNights}
          />
        </AddToTripButtonContainer>
      )}
      <OfferCard
        id={offerId}
        idx={index}
        variant="card-default"
        size="small"
        lazyLoad={index > 1}
        selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
      />
    </HotelCardWrapper>
  )
}

export default memo(ChatHotelCarouselCard)
